import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Button, Carousel } from "react-bootstrap";
import "./index.scss";
import { LeftIcon } from "../../../assets/icons/LeftIcon";
import { RightIcon } from "../../../assets/icons/RightIcon";
import { FacebookIcon } from "../../../assets/icons/FacebookIcon";
import { InstaIcon } from "../../../assets/icons/InstaIcon";
import { PartageIcon } from "../../../assets/icons/PartageIcon";
import { FaLinkedinIn } from "react-icons/fa";
import { LinkIcon } from "../../../assets/icons/LinkIcon";
import { ZomeIcon } from "../../../assets/icons/ZomeIcon";
import video1 from "../../../assets/images/video1.mp4";
import videoPoster from "../../../assets/images/video-poster.png"; // poster image
import img from "../../../assets/images/slid1.png";
import img1 from "../../../assets/images/img1.png";
import img2 from "../../../assets/images/img2.jpg";
import img3 from "../../../assets/images/img3.jpg";
import Header from "../../appLayout/header";
import GallerySection from "./GallerySection";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../hooks/useTranslation";
import logocorporate from "../../../assets/images/logocorporate.png";

export default function HeroSection() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const socialLinks = {
    instagram: "https://www.instagram.com/spot.inspace/",
    facebook: "https://www.facebook.com/profile.php?id=61575400013312",
    linkedin: "https://www.linkedin.com/company/spotin-coworking/",
  };

  const slides = [
    { id: 1, img: img, title: language === "fr" ? "Espace coworking lumineux" : "Bright coworking space" },
    { id: 2, img: img1, title: language === "fr" ? "Kitchinette bien équipée" : "Well-equipped kitchenette" },
    { id: 3, img: img2, title: language === "fr" ? "Espace coworking lumineux" : "Bright coworking space" },
    { id: 4, img: img3, title: language === "fr" ? "Salle de réunion moderne" : "Modern meeting room" },
  ];

  const [index, setIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileThumbIndex, setMobileThumbIndex] = useState(0);
  const thumbVisibleCount = 3;

  // 🔹 Check if device is mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 🔹 Safe orientation lock
  const lockToLandscape = () => {
    if (isMobile && window.screen?.orientation?.lock) {
      window.screen.orientation.lock("landscape").catch(() => {
        console.log("Orientation lock not supported on this device");
      });
    }
  };
  const unlockOrientation = () => {
    if (isMobile && window.screen?.orientation?.unlock) {
      window.screen.orientation.unlock();
    }
  };

  // 🔹 Pause / play video safely
  useEffect(() => {
    const activeVideo = videoRef.current;
    if (!activeVideo) return;
    if (showGallery) {
      activeVideo.pause();
    } else {
      const playPromise = activeVideo.play();
      if (playPromise?.catch) {
        playPromise.catch((error) => console.log("Video autoplay prevented on mobile:", error));
      }
    }
  }, [showGallery, index]);

  // 🔹 Lock orientation when gallery opens
  useEffect(() => {
    if (showGallery && isMobile) lockToLandscape();
    if (!showGallery && isMobile) unlockOrientation();
    return () => unlockOrientation();
  }, [showGallery, isMobile]);

  // 🔹 Sync mobile thumbnail index with main carousel
  useEffect(() => {
    if (isMobile) setMobileThumbIndex(index);
  }, [index, isMobile]);

  const handleSelect = (selectedIndex) => setIndex(selectedIndex);

  const handleThumbPrev = () => {
    if (isMobile) {
      setMobileThumbIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    } else {
      setThumbStart((prev) => (prev === 0 ? slides.length - thumbVisibleCount : prev - 1));
    }
  };

  const handleThumbNext = () => {
    if (isMobile) {
      setMobileThumbIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    } else {
      setThumbStart((prev) => (prev + thumbVisibleCount >= slides.length ? 0 : prev + 1));
    }
  };

  const visibleThumbnails = isMobile
    ? [slides[mobileThumbIndex]]
    : slides.slice(thumbStart, thumbStart + thumbVisibleCount);

  return (
    <>
      <Header />
      <section className={`hero-section ${showGallery ? "blurred" : ""}`}>
        <Carousel activeIndex={index} onSelect={handleSelect} controls={false} indicators={false} interval={null}>
          {slides.map((slide, i) => (
            <Carousel.Item key={slide.id}>
              <video
                ref={videoRef}
                className="d-block w-100 hero-video"
                src={video1}
                poster={videoPoster}
                muted
                loop
              />
            </Carousel.Item>
          ))}
        </Carousel>

        <div className="hero-content w-100 h-100">
          <Row>
            <Col md={10}>
              <div className="hero-text">
                <h1>
                  {t("home.title")}
                  <br />
                  {t("home.title2")}
                </h1>
                <p>{t("home.subtitle")}</p>
                <Button variant="dark" className="reserve-btn" onClick={() => navigate("/contact")}>
                  {t("home.reserveButton")}
                </Button>
              </div>
            </Col>

            <Col md={2} className="social-icons">
              <div className="icon">
                <PartageIcon />
              </div>
              <div className="icon-section">
                <div className="icon1">
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <InstaIcon />
                  </a>
                </div>
                <div className="icon1">
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                    <FacebookIcon />
                  </a>
                </div>
                <div className="icon1">
                  <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <FaLinkedinIn color="#fff" size={18} />
                  </a>
                </div>
              </div>
            </Col>
          </Row>

          {/* 🔹 Miniatures */}
          <div className="thumbnail-slider">
            <Button className="arrow left" onClick={handleThumbPrev}>
              <LeftIcon />
            </Button>

            <div className="thumbnails">
              {visibleThumbnails.map((slide, i) => {
                const realIndex = isMobile ? mobileThumbIndex : (thumbStart + i) % slides.length;
                const isActive = isMobile ? mobileThumbIndex === index : realIndex === index;
                return (
                  <div className="thumbnail-wrapper" key={slide.id}>
                    <img
                      src={slide.img}
                      alt={`Thumb ${slide.id}`}
                      className={isActive ? "active" : ""}
                      onClick={() => setIndex(realIndex)}
                    />
                    <div
                      className="icon-zome"
                      onClick={() => {
                        setCurrentGalleryIndex(realIndex);
                        setShowGallery(true);
                      }}
                    >
                      <ZomeIcon />
                    </div>
                  </div>
                );
              })}
            </div>

            <Button className="arrow right" onClick={handleThumbNext}>
              <RightIcon />
            </Button>
          </div>
        </div>
      </section>

      {/* <div className="btn-direction">
        <Button className="btn-style">
          <p>MSITCORPORATE.COM</p>
          <div className="default-icon">
            <LinkIcon />
          </div>
          <div className="hover-icon">
            <img src={logocorporate} alt="Spot In Logo" />
          </div>
        </Button>
      </div> */}

      {/* 🔹 Gallery overlay */}
      {showGallery && (
        <div
          className="gallery-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("gallery-overlay")) {
              setShowGallery(false);
              if (isMobile) unlockOrientation();
            }
          }}
        >
          <div
            className="close-btn"
            onClick={() => {
              setShowGallery(false);
              if (isMobile) unlockOrientation();
            }}
          >
            ✕
          </div>
          <GallerySection images={slides} initialIndex={currentGalleryIndex} isMobile={isMobile} />
        </div>
      )}
    </>
  );
}
